'use client';
import type { GetStaticProps } from 'next';
import * as React from 'react';
import { useRouter } from 'next/router';

import PageContainer from '@/components/PageContainer';
import config from '@/config/config';
import { queryFooter, queryNavbar, queryPageBySlug, querySectionsBySystemIds } from '@/clients/queries';
import {
  ActiveIncidentQuery,
  ActiveIncidentQueryVariables,
  FooterQuery,
  NavbarQuery,
  PageFieldsFragment,
  PageSectionsBySystemIdQuery,
} from 'types/generated';
import { getClient } from '@/utils/contentful';
import { chunk } from '@/utils/arrays';
import { getOr } from '@/utils/options';
import { render } from '@/contentful';
import { useQuery } from '@apollo/client';
import { ActiveIncident } from '@/clients/queries/contentful/ActiveIncident.graphql';
import ToastPopup from '@/components/Incidents/IncidentPopup';
import { getRadioSchedules } from '@/clients/RadioUtils';
import { RadioSchedule } from '@/models/RadioTypes';
import { AnimatePresence } from 'framer-motion';
import { Log } from '@/logging/datadog';

type Props = {
  page: PageFieldsFragment;
  navbar: NavbarQuery;
  footer: FooterQuery;
  sections: Array<PageSectionsBySystemIdQuery>;
  orderedIds: Array<string>;
  radioSchedules: Record<string, RadioSchedule[]>;
};

const IndexPage = ({ navbar, footer, page, sections, orderedIds, radioSchedules }: Props) => {
  const { data } = useQuery<ActiveIncidentQuery, ActiveIncidentQueryVariables>(ActiveIncident);

  const [showActiveIncidentModal, setShowActiveIncidentModal] = React.useState<boolean>(false);
  const router = useRouter();

  // REMOVE LATER KV ZAKKI
  React.useEffect(() => {
    if (!router.isReady) return;
    const pageid = router.query.pageid as string | undefined;
    if (pageid === 'cbb0598f-245f-11e4-80bf-005056af692d') {
      window.location.href = `https://verslun.syn.is/?pageid=${pageid}`;
    }
  }, [router.isReady, router.query]);

  const handleActiveIncidentModalClose = () => {
    setShowActiveIncidentModal(false);
  };

  React.useEffect(() => {
    if (data?.incidentCollection?.items?.length) {
      setShowActiveIncidentModal(true);
    }
  }, [data]);

  const activeIncident = data?.incidentCollection?.items?.[0];

  return (
    <>
      <PageContainer
        title={getOr(page.title, 'Sýn')}
        metaTags={page.metaTags}
        contactFormSetting={page.contactFormSetting}
        navbar={navbar}
        footer={footer}
        radioSchedules={radioSchedules}
      >
        {render(sections, orderedIds)}
      </PageContainer>
      <AnimatePresence>
        {activeIncident && showActiveIncidentModal && (
          <ToastPopup
            title={getOr(activeIncident.title, '')}
            description={activeIncident.markdownDescription}
            timeStamp={getOr(activeIncident.timestamp, '')}
            status={getOr(activeIncident.status, '')}
            onClose={handleActiveIncidentModalClose}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const inPreview = getOr(context.draftMode, false);
  try {
    const client = getClient(inPreview);

    const slug = '/';
    const contentfulPage = (await queryPageBySlug(client, slug, inPreview)).data;

    if (!contentfulPage.pageCollection) {
      throw new Error(`no page collection found in graphql response when fetching page details for ${slug}`);
    }

    if (!contentfulPage.pageCollection.items) {
      throw new Error(`no items array set in page details for ${slug}`);
    }

    const page = contentfulPage.pageCollection.items[0] || undefined;

    if (!page) {
      throw new Error(`no page component found in graphql response for slug ${slug}`);
    }

    const sectionIds = (page.sectionsCollection?.items.map((x) => x?.sys.id) || []) as string[];

    const chunkedSections = chunk(sectionIds, config.SECTION_CHUNK_SIZE).map((c) => {
      return querySectionsBySystemIds(client, c, inPreview).then((x) => x.data);
    });

    return {
      props: {
        navbar: (await queryNavbar(client)).data,
        footer: (await queryFooter(client)).data,
        page,
        sections: await Promise.all(chunkedSections),
        orderedIds: sectionIds,
        radioSchedules: await getRadioSchedules(),
      },
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);
    throw err;
  }
};

export default IndexPage;
